/**
 * ReportMarkdown — renders a research run's markdown report outside the
 * assistant-ui message context (which MarkdownText is bound to).
 *
 * Citation handling per DEEP_RESEARCH_REDESIGN.md decision 2: the agent cites
 * findings as [F1], [F2], … and sources as [S1], [S2], …. These become chips —
 * [F#] switches to the findings tab, [S#] jumps to the sources section.
 * Citations that don't resolve (the model occasionally invents an [F5] with
 * only 4 findings) render as plain text, never as dead links.
 */

import { memo, useMemo } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import { useParams } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { useDocumentOverlayOptional } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { EvidenceReferenceButton } from '@/components/evidence/EvidenceReferenceButton'
import {
  documentMoment,
  isInAppReference,
  sourcePreviewReference,
} from '@/features/source-snippets/reference'

const FINDING_HREF = 'research-finding'
const SOURCE_HREF = 'research-source'

// react-markdown's default URL sanitizer strips any non-standard scheme — it
// would blank out our internal citation hrefs (research-finding://, node:, …)
// before the `a` renderer can turn them into chips, leaving dead underlined
// links. Allow our schemes through; keep default sanitization for the rest.
// `doc:` and `evidence:` are the references the chat draws too (`isInAppReference`).
const REPORT_HREF = /^(research-finding|research-source|node):/
function reportUrlTransform(url: string): string {
  return isInAppReference(url) || REPORT_HREF.test(url) ? url : defaultUrlTransform(url)
}

export interface ReportMarkdownProps {
  markdown: string
  /** How many findings exist — [F#] beyond this renders as plain text. */
  findingCount: number
  /** How many sources exist — [S#] beyond this renders as plain text. */
  sourceCount: number
  onOpenFinding?: (index: number) => void
  onOpenSource?: (index: number) => void
  /** Entity drill-in for `node:<elementId>` links the model writes in the
   * report body. Without it those links render as plain text. */
  onOpenEntityRef?: (elementId: string) => void
}

/** Turn [F3] / [S2] into markdown links our `a` renderer intercepts. */
function linkifyCitations(markdown: string): string {
  return markdown
    .replace(/\[F(\d+)\]/g, `[F$1](${FINDING_HREF}://$1)`)
    .replace(/\[S(\d+)\]/g, `[S$1](${SOURCE_HREF}://$1)`)
}

function CitationChip({ label, onClick }: { label: string; onClick?: () => void }) {
  if (!onClick) return <span>[{label}]</span>
  // The chip is ~18 px tall inside running text, so below md an invisible
  // pseudo-element widens its tap area without moving the text.
  return (
    <button
      type="button"
      onClick={onClick}
      className="mx-0.5 inline-flex -translate-y-px items-center rounded bg-primary/10 px-1 font-mono text-[11px] font-medium text-primary hover:bg-primary/20 max-md:relative max-md:after:absolute max-md:after:-inset-x-1 max-md:after:-inset-y-1.5"
    >
      {label}
    </button>
  )
}

export const ReportMarkdown = memo(function ReportMarkdown({
  markdown,
  findingCount,
  sourceCount,
  onOpenFinding,
  onOpenSource,
  onOpenEntityRef,
}: ReportMarkdownProps) {
  const processed = useMemo(() => linkifyCitations(markdown), [markdown])
  const docOverlay = useDocumentOverlayOptional()
  const { caseId } = useParams<{ caseId: string }>()

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-3 break-words text-sm leading-relaxed text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={reportUrlTransform}
        components={{
          h1: ({ children }) => <h1 className="text-lg font-semibold">{children}</h1>,
          h2: ({ children }) => <h2 className="mt-2 text-base font-semibold">{children}</h2>,
          h3: ({ children }) => <h3 className="mt-1 text-sm font-semibold">{children}</h3>,
          p: ({ children }) => <p>{children}</p>,
          ul: ({ children }) => <ul className="flex list-disc flex-col gap-1 pl-5">{children}</ul>,
          ol: ({ children }) => (
            <ol className="flex list-decimal flex-col gap-1 pl-5">{children}</ol>
          ),
          code: ({ children }) => (
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{children}</code>
          ),
          // Wide GFM tables (the model loves element-id columns) must scroll
          // inside the card/sheet instead of blowing the layout open.
          table: ({ children }) => (
            <div className="w-full overflow-x-auto rounded-md border border-border/60">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border-b border-border/60 bg-muted/40 px-2 py-1.5 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border-b border-border/30 px-2 py-1.5 align-top font-mono text-2xs">
              {children}
            </td>
          ),
          a: ({ href, children }) => {
            const preview = sourcePreviewReference(href, caseId)
            if (preview)
              return (
                <EvidenceReferenceButton caseId={preview.caseId} referenceId={preview.referenceId}>
                  {children}
                </EvidenceReferenceButton>
              )
            if (href?.startsWith(`${FINDING_HREF}://`)) {
              const index = Number(href.slice(`${FINDING_HREF}://`.length))
              const valid = Number.isFinite(index) && index >= 1 && index <= findingCount
              return (
                <CitationChip
                  label={`F${index}`}
                  onClick={valid && onOpenFinding ? () => onOpenFinding(index) : undefined}
                />
              )
            }
            if (href?.startsWith(`${SOURCE_HREF}://`)) {
              const index = Number(href.slice(`${SOURCE_HREF}://`.length))
              const valid = Number.isFinite(index) && index >= 1 && index <= sourceCount
              return (
                <CitationChip
                  label={`S${index}`}
                  onClick={valid && onOpenSource ? () => onOpenSource(index) : undefined}
                />
              )
            }
            // The model writes node:/doc: refs as links. A raw <a> would
            // resolve them as relative URLs (navigating to the agent page in
            // a new tab) — open the in-app views instead.
            if (href?.startsWith('evidence:') && caseId) {
              return (
                <EvidenceReferenceButton caseId={caseId} referenceId={href.slice(9)}>
                  {children}
                </EvidenceReferenceButton>
              )
            }
            if (href?.startsWith('node:')) {
              const elementId = href.slice('node:'.length)
              return (
                <CitationChip
                  label={String(children)}
                  onClick={onOpenEntityRef ? () => onOpenEntityRef(elementId) : undefined}
                />
              )
            }
            const moment = documentMoment(href)
            if (moment) {
              return (
                <CitationChip
                  label={String(children)}
                  onClick={docOverlay ? () => docOverlay.open(moment) : undefined}
                />
              )
            }
            if (href && !/^https?:/i.test(href)) {
              // Unknown non-http scheme — never a navigable link.
              return <span>{children}</span>
            }
            return (
              <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">
                {children}
              </a>
            )
          },
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  )
})
